import React, { useState, useEffect } from "react";
import axios from "axios";
import * as Yup from "yup";
import { useTranslation } from "react-i18next";
import { TextInput, Select, Textarea, Button, Stack } from "@mantine/core";
import { BasicForm } from "@components/shared";

import {
  DESCRIPTOR_COGNITION,
  DESCRIPTOR_PERSONALITY,
  questionDescriptorOptions,
} from "@util/data/questionDescriptor";

import {
  MULTIPLE_CHOICE_VALUE,
  TRUE_FALSE_VALUE,
  YES_NO_VALUE,
  OPEN_RESPONSE_VALUE,
  questionTypeOptions,
} from "@util/data/questionType";

import surveyVariety, {
  ENGAGEMENT_VALUE,
  CLEARSCREEN_VALUE,
  COMPETENCY_ASSESSMENT_VALUE,
  PERFORMANCE_REVIEW,
} from "@util/data/surveyVariety";

const HAS_CORRECT_ANSWER_NO = false;
const HAS_CORRECT_ANSWER_YES = true;

import { IconCircleCheck } from "@tabler/icons-react";

export default function QuestionForm({ id, question, onSuccess }) {
  const { t } = useTranslation();
  const [loading, setLoading] = useState(false);
  const [questionType, setQuestionType] = useState(null);
  const [formValues, setFormValues] = useState({
    a: "Strongly Disagree",
    b: "Disagree",
    c: "Neutral",
    d: "Agree",
    e: "Strongly Agree",
    f: "",
    g: "",
    h: "",
    i: "",
    j: "",
    k: "",
    categoryString: "",
    hasComment: false,
    hasCorrectAnswer: HAS_CORRECT_ANSWER_NO,
    correctAnswer: null,
    descriptor: DESCRIPTOR_PERSONALITY,
    variety: CLEARSCREEN_VALUE,
    text: "",
    dimension: null,
    factor: null,
  });
  const [multiMode, setMultiMode] = useState("clearscreen");

  useEffect(() => {
    setMultipleChoiceAnswers();
  }, [multiMode]);

  function setMultipleChoiceAnswers() {
    if (multiMode === "clearscreen") {
      setFormValues({
        ...formValues,
        a: "Strongly Disagree",
        b: "Disagree",
        c: "Neutral",
        d: "Agree",
        e: "Strongly Agree",
      });
    } else if (multiMode === "360") {
      setFormValues({
        ...formValues,
        a: "1",
        b: "2",
        c: "3",
        d: "4",
        e: "5",
      });
    } else {
      setFormValues({
        ...formValues,
        a: "Unacceptable",
        b: "Below Expectations",
        c: "Meets Expectations",
        d: "Exceeds Expectations",
        e: "Exemplary",
      });
    }
  }

  function onSubmit(formData) {
    setLoading(true);

    if (id) return onUpdate(formData);

    return onCreate(formData);
  }

  function onCreate(formData) {
    const req = {
      ...formData,
    };

    axios
      .post(`/questions/`, req)
      .then(({ data }) => {
        onSuccess(data.response[0].id);
      })
      .then(() => setLoading(false))
      .catch((err) => {
        setLoading(false);
        toast.error(err);
      });
  }

  function onUpdate(formData) {
    const req = {
      ...formData,
      id,
    };

    axios
      .put(`/questions/${id}/`, req)
      .then(() => setLoading(false))
      .then(() => onSuccess())
      .catch((err) => {
        setLoading(false);
        toast.error(err);
      });
  }

  const fields = [
    {
      name: "question",
      label: "Question",
      initialValue: question,
      required: true,
      schema: () => Yup.string().required("Required"),
    },
  ];

  return (
    <div>
      <form onSubmit={(e) => e.preventDefault()}>
        <Stack>
          <Select
            placeholder={t("select")}
            value={questionType}
            onChange={(value) => setQuestionType(value)}
            data={questionTypeOptions}
            label="What type of question?"
          />
        </Stack>
      </form>
    </div>
  );

  return (
    <div>
      <BasicForm
        loading={loading}
        onSubmit={onSubmit}
        fields={fields}
        buttonProps={{
          fullWidth: id ? false : true,
        }}
      />
    </div>
  );
}

const dimensionOptions = [
  { key: 0, text: "Organization", value: 0 },
  { key: 2, text: "Self", value: 2 },
  { key: 1, text: "Team", value: 1 },
];

const factorOptions = [
  { key: 0, text: "Cohesion", value: 0 },
  { key: 3, text: "Communication", value: 3 },
  { key: 6, text: "Custom", value: 6 },
  { key: 2, text: "Development", value: 2 },
  { key: 4, text: "Resources", value: 4 },
  { key: 1, text: "Supervision", value: 1 },
  { key: 5, text: "Workload", value: 5 },
];

const ChoiceLabel = (props) => {
  return (
    <label className="choice-label">
      {props.text}
      {props.showButton && (
        <button
          className={props.isCorrectAnswer ? "selected" : ""}
          onClick={props.onClick}
          title="Mark correct answer"
        >
          <IconCircleCheck />
        </button>
      )}
    </label>
  );
};
